import { Plugin } from '../../core/plugin-interface.js';
// Resource info interface removed - not used
/**
 * ResourceHintsPlugin - Adds resource hints to improve loading performance
 * Implements predictive resource loading and connection warming
 */
export class ResourceHintsPlugin extends Plugin {
    constructor(config) {
        super({
            ...config
        });
        this.name = 'ResourceHintsPlugin';
        this.config = {
            preconnectDomains: [],
            dnsPrefetchDomains: [],
            enableWellKnownDomains: true,
            ...config
        };
        this.addedHints = new Set();
    }
    /**
     * Initialize the plugin
     */
    async init() {
        const startTime = performance.now();
        try {
            this.log('Resource hints plugin initialized', 'debug');
        }
        finally {
            this.metrics.initTime = performance.now() - startTime;
        }
    }
    /**
     * Execute resource hint optimizations
     */
    async execute() {
        const startTime = performance.now();
        try {
            // Add static preconnect and dns-prefetch hints
            this.addStaticHints();
            this.log(`Added ${this.addedHints.size} resource hints`, 'debug');
        }
        finally {
            this.metrics.executeTime = performance.now() - startTime;
        }
    }
    /**
     * Add static preconnect and dns-prefetch hints
     */
    addStaticHints() {
        const allDomains = [];
        // Add user-specified domains
        allDomains.push(...(this.config.preconnectDomains || []));
        allDomains.push(...(this.config.dnsPrefetchDomains || []));
        // Add well-known domains if enabled
        if (this.config.enableWellKnownDomains) {
            allDomains.push(...ResourceHintsPlugin.WELL_KNOWN_DOMAINS.fonts);
            allDomains.push(...ResourceHintsPlugin.WELL_KNOWN_DOMAINS.cdn);
        }
        // Remove duplicates
        const uniqueDomains = [...new Set(allDomains)];
        // Add preconnect hints for user-specified domains
        (this.config.preconnectDomains || []).forEach(domain => {
            this.addResourceHint('preconnect', domain);
        });
        // Add preconnect hints for well-known domains if enabled
        if (this.config.enableWellKnownDomains) {
            ResourceHintsPlugin.WELL_KNOWN_DOMAINS.fonts.forEach(domain => {
                this.addResourceHint('preconnect', domain);
            });
            ResourceHintsPlugin.WELL_KNOWN_DOMAINS.cdn.forEach(domain => {
                this.addResourceHint('preconnect', domain);
            });
        }
        // Add dns-prefetch as fallback for all domains not already preconnected
        uniqueDomains.forEach(domain => {
            const isPreconnected = (this.config.preconnectDomains || []).includes(domain) ||
                (this.config.enableWellKnownDomains &&
                    [...ResourceHintsPlugin.WELL_KNOWN_DOMAINS.fonts,
                        ...ResourceHintsPlugin.WELL_KNOWN_DOMAINS.cdn].includes(domain));
            if (!isPreconnected) {
                this.addResourceHint('dns-prefetch', domain);
            }
        });
        // Add explicit dns-prefetch domains
        (this.config.dnsPrefetchDomains || []).forEach(domain => {
            this.addResourceHint('dns-prefetch', domain);
        });
    }
    /**
     * Add a resource hint
     */
    addResourceHint(rel, href) {
        const key = `${rel}:${href}`;
        if (this.addedHints.has(key))
            return;
        const link = document.createElement('link');
        link.rel = rel;
        link.href = href;
        document.head.appendChild(link);
        this.addedHints.add(key);
        this.log(`Added ${rel} hint for ${href}`, 'debug');
    }
    /**
     * Clean up plugin resources
     */
    async cleanup() {
        await super.cleanup();
        // Remove added hints
        const links = document.querySelectorAll('link[rel="preconnect"], link[rel="dns-prefetch"]');
        links.forEach(link => {
            const linkEl = link;
            const key = `${linkEl.rel}:${linkEl.href}`;
            if (this.addedHints.has(key)) {
                link.remove();
            }
        });
        this.addedHints.clear();
    }
}
// Essential well-known domains for performance-critical resources
ResourceHintsPlugin.WELL_KNOWN_DOMAINS = {
    fonts: [
        'https://fonts.googleapis.com',
        'https://fonts.gstatic.com',
        'https://use.typekit.net'
    ],
    cdn: [
        'https://cdn.jsdelivr.net',
        'https://cdnjs.cloudflare.com',
        'https://ajax.googleapis.com'
    ]
};
//# sourceMappingURL=index.js.map